import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useEffect, useState } from "react";
import { PracticeTypeRuleModality } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";
import {
  IProcedureModalityRange,
  IProcedureModalityRangeSaveRequest,
} from "types/ProcedureModalityRange/ProcedureModalityRange";

import {
  IRangeDraft,
  PRECEDENCE_SENTENCE,
  rangeDraftProblem,
  rangeToSaveRequest,
  toRangeDraft,
} from "./cptGroupWording";
import { MODALITY_CHOICES } from "./serviceTypeRuleWording";

interface ICptGroupDialogProps {
  visible: boolean;
  /** The range being edited, or null when adding. */
  range: IProcedureModalityRange | null;
  /** Every saved range, for the duplicate check. */
  ranges: IProcedureModalityRange[];
  /** A NEW range starts in this group (the tab's filter). */
  defaultModality: PracticeTypeRuleModality | null;
  saving: boolean;
  onSave: (body: IProcedureModalityRangeSaveRequest) => void;
  onHide: () => void;
}

/**
 * CLE-3000 — Add / Edit a CPT group range. Mirrors the Service Type rule dialog: a draft held
 * locally, checked here with the API's own wording before it is sent, and the whole row sent on
 * save.
 */
export function CptGroupDialog({
  visible,
  range,
  ranges,
  defaultModality,
  saving,
  onSave,
  onHide,
}: ICptGroupDialogProps) {
  const [draft, setDraft] = useState<IRangeDraft>(
    toRangeDraft(range, defaultModality)
  );
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setDraft(toRangeDraft(range, defaultModality));
      setProblem(null);
    }
  }, [visible, range, defaultModality]);

  const set = (patch: Partial<IRangeDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const save = () => {
    const found = rangeDraftProblem(draft, ranges, range?.id ?? null);
    setProblem(found);
    if (found) return;
    onSave(rangeToSaveRequest(draft));
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        data-testid="cptGroups.dialog.cancel"
      />
      <Button
        type="button"
        label={range ? "Save range" : "Add range"}
        loading={saving}
        onClick={save}
        data-testid="cptGroups.dialog.save"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={range ? "Edit CPT group range" : "Add a CPT group range"}
      footer={footer}
      style={{ width: "min(560px, 95vw)" }}
      data-testid="cptGroups.dialog"
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-1">
          <Label htmlFor="cptGroups.modality">Group</Label>
          <Dropdown
            inputId="cptGroups.modality"
            options={MODALITY_CHOICES}
            value={draft.modality}
            onChange={(event) => set({ modality: event.value ?? null })}
            placeholder="Choose a group"
          />
          <small className="text-color-secondary">{PRECEDENCE_SENTENCE}</small>
        </div>

        <div className="grid">
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="cptGroups.fromCode">From</Label>
            <InputText
              id="cptGroups.fromCode"
              data-testid="cptGroups.fromCode"
              value={draft.fromCode}
              onChange={(event) => set({ fromCode: event.target.value })}
              placeholder="70540"
            />
          </div>
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="cptGroups.toCode">To (optional)</Label>
            <InputText
              id="cptGroups.toCode"
              data-testid="cptGroups.toCode"
              value={draft.toCode}
              onChange={(event) => set({ toCode: event.target.value })}
              placeholder="Same as From"
            />
          </div>
          <small className="col-12 pt-0 text-color-secondary">
            Inclusive. Five digits, or a HCPCS letter and four digits; both ends
            the same kind. Leave To empty for one code.
          </small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="cptGroups.note">Note (optional)</Label>
          <InputText
            id="cptGroups.note"
            data-testid="cptGroups.note"
            value={draft.note}
            onChange={(event) => set({ note: event.target.value })}
            placeholder="TMJ MRI, nested in the head X-ray range"
          />
          <small className="text-color-secondary">
            One line about the codes, never about a patient.
          </small>
        </div>

        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="cptGroups.enabled"
            checked={draft.enabled}
            onChange={(event) => set({ enabled: !!event.checked })}
          />
          <label htmlFor="cptGroups.enabled">Enabled</label>
        </div>

        {problem && (
          <Message
            severity="error"
            text={problem}
            data-testid="cptGroups.dialog.problem"
          />
        )}
      </div>
    </Dialog>
  );
}
